import {
  ORDEM_ARQUIVO_PRINCIPAL,
  ORDEM_PROCESSAMENTO_DOCUMENTOS,
  TIPOS_DOCUMENTO_LICITACAO,
} from '@/lib/licitacao-documentos.types.js';

/**
 * Normaliza arquivos_relacionados legados (sem tipo) para lista tipada.
 * @param {unknown} arquivosRelacionados
 * @param {string|null} arquivoOriginal
 * @returns {Array<{ tipo: string, url: string, nome: string, content_sha256?: string }>}
 */
export function normalizarArquivosRelacionadosLegado(arquivosRelacionados, arquivoOriginal = null) {
  const lista = Array.isArray(arquivosRelacionados) ? arquivosRelacionados : [];
  const out = [];
  const urlsVistas = new Set();

  const push = (entry) => {
    if (!entry?.url || urlsVistas.has(entry.url)) return;
    urlsVistas.add(entry.url);
    out.push(entry);
  };

  if (arquivoOriginal) {
    const jaNoArray = lista.some(
      (a) => (a?.url || a?.file_url) === arquivoOriginal
    );
    if (!jaNoArray) {
      push({
        tipo: 'edital',
        url: arquivoOriginal,
        nome: 'Arquivo original',
      });
    }
  }

  lista.forEach((a, index) => {
    const url = a?.url || a?.file_url;
    if (!url) return;
    let tipo = a?.tipo || a?.slot;
    if (!tipo || !TIPOS_DOCUMENTO_LICITACAO.includes(tipo)) {
      if (index === 0 && !out.some((x) => x.tipo === 'edital')) tipo = 'edital';
      else if (index === 1 && !out.some((x) => x.tipo === 'etp')) tipo = 'etp';
      else if (index === 2 && !out.some((x) => x.tipo === 'termo_referencia')) tipo = 'termo_referencia';
      else tipo = 'edital';
    }
    push({
      tipo,
      url,
      nome: a?.nome || a?.name || 'Documento',
      content_sha256: a?.content_sha256 || a?.contentSha256,
    });
  });

  return out;
}

/**
 * Monta documentos para upload/create a partir do slot.
 * @param {Partial<Record<'edital'|'etp'|'termo_referencia', File>>} documentosSlot
 * @returns {Array<{ tipo: string, file: File, nome: string }>}
 */
export function resolverDocumentosUpload(documentosSlot) {
  if (!documentosSlot || typeof documentosSlot !== 'object') return [];
  return ORDEM_PROCESSAMENTO_DOCUMENTOS.filter((tipo) => documentosSlot[tipo]).map((tipo) => ({
    tipo,
    file: documentosSlot[tipo],
    nome: documentosSlot[tipo].name,
  }));
}

/**
 * @deprecated Use resolverDocumentosUpload com files[] legado.
 */
export function resolverDocumentosUploadLegadoFiles(files) {
  if (!Array.isArray(files) || files.length === 0) return [];
  return files.map((file, index) => {
    let tipo = 'edital';
    if (index === 1) tipo = 'etp';
    if (index === 2) tipo = 'termo_referencia';
    if (index > 2) tipo = 'termo_referencia';
    return { tipo, file, nome: file.name };
  });
}

/**
 * Documentos para reprocessamento: novos File substituem; senão URL existente por tipo.
 * @param {object} edital
 * @param {Partial<Record<'edital'|'etp'|'termo_referencia', File>>} novosArquivos
 */
export function resolverDocumentosReprocesso(edital, novosArquivos = {}) {
  const relacionados = normalizarArquivosRelacionadosLegado(
    edital?.arquivos_relacionados ?? edital?.arquivosRelacionados,
    edital?.arquivo_original ?? edital?.arquivoOriginal
  );

  const porTipo = new Map();
  relacionados.forEach((r) => porTipo.set(r.tipo, r));

  const resultado = [];
  for (const tipo of ORDEM_PROCESSAMENTO_DOCUMENTOS) {
    const novoFile = novosArquivos?.[tipo];
    if (novoFile) {
      resultado.push({ tipo, file: novoFile, nome: novoFile.name });
      continue;
    }
    const existente = porTipo.get(tipo);
    if (existente?.url) {
      resultado.push({
        tipo,
        url: existente.url,
        nome: existente.nome,
        content_sha256: existente.content_sha256,
      });
    }
  }

  return resultado;
}

/**
 * Primeiro documento na ordem edital → TR → ETP (arquivo principal).
 */
export function obterDocumentoPrincipal(documentos) {
  for (const tipo of ORDEM_ARQUIVO_PRINCIPAL) {
    const doc = documentos.find((d) => d.tipo === tipo);
    if (doc) return doc;
  }
  return documentos[0] ?? null;
}

export function validarMinimoUmDocumento(documentos) {
  return Array.isArray(documentos) && documentos.length > 0;
}
